import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { clsx } from 'clsx';
import type { PrintViewerProps } from './MasterDocumentPrinting.types';
import { useMasterDocumentTranslation, MASTER_DOCUMENT_TRANSLATIONS_ES } from '../i18n';

// CSS scope-ado del visor: oculta el bloque gris del skeleton interno del
// ReportViewer y centra un spinner + texto, para una espera limpia. Replica el
// comportamiento del PrintIexModal de core.
const PRINT_VIEWER_CSS = `
.sds-print-viewer { height: 100%; }
.sds-print-viewer [data-testid="report-viewer-skeleton"] { animation: none; gap: .875rem; }
.sds-print-viewer [data-testid="report-viewer-skeleton"] > div:first-child { display: none; }
.sds-print-viewer [data-testid="report-viewer-skeleton"]::before {
  content: ""; width: 2.75rem; height: 2.75rem; border-radius: 9999px;
  border: 3px solid rgb(226 232 240); border-top-color: #0e79fd;
  animation: sds-print-spin .7s linear infinite;
}
.sds-print-viewer [data-testid="report-viewer-skeleton"] p { font-size: .9375rem; font-weight: 500; color: rgb(71 85 105); margin: 0; }
@keyframes sds-print-spin { to { transform: rotate(360deg); } }
`;

/**
 * Mensaje por defecto cuando la impresión no está configurada.
 *
 * Es API pública del kit (se re-exporta hasta `src/index.ts`), así que sigue siendo
 * una constante en español y no se puede resolver con un hook. Su valor se toma del
 * catálogo para no duplicar el literal; el render usa `t()` sobre la misma clave,
 * de modo que la UI sí respeta el idioma activo.
 *
 * @see MASTER_DOCUMENT_TRANSLATIONS_ES
 */
export const DEFAULT_NOT_CONFIGURED_MESSAGE =
  MASTER_DOCUMENT_TRANSLATIONS_ES['master_document.document.printNotImplemented'];

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface MasterDocumentPrintModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  /**
   * `true` cuando el backend/visor está configurado (hay viewer + apiBaseUrl +
   * reportCode resueltos). `false` ⇒ se muestra el estado "no configurado".
   */
  backendConfigured: boolean;
  /** Mensaje del estado "no configurado". */
  notConfiguredMessage?: string;
  /** El host pasa el visor ya resuelto (componente o render-prop) más sus props. */
  viewer?: ComponentType<PrintViewerProps>;
  renderViewer?: (props: PrintViewerProps) => ReactNode;
  viewerProps: PrintViewerProps;
}

/**
 * Marco del modal de vista previa de impresión, propiedad del kit. Replica el UX
 * del `PrintIexModal` de core (overlay, maximizar/restaurar, cerrar) y añade
 * accesibilidad que core no tenía: `role="dialog"` + `aria-modal`, cierre con
 * `Esc`, focus trap y restauración de foco al disparador.
 *
 * El **contenido** (visor de reportes) lo aporta el host por inyección; el kit
 * nunca importa `@siesateams/format-designer`.
 */
export function MasterDocumentPrintModal({
  isOpen,
  title,
  onClose,
  backendConfigured,
  notConfiguredMessage,
  viewer: Viewer,
  renderViewer,
  viewerProps,
}: MasterDocumentPrintModalProps) {
  const { t } = useMasterDocumentTranslation();
  const [isMaximized, setIsMaximized] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Reinicia el tamaño al cerrar para que la próxima apertura sea consistente.
  useEffect(() => {
    if (!isOpen) setIsMaximized(false);
  }, [isOpen]);

  // Esc + focus trap + restauración de foco.
  useEffect(() => {
    if (!isOpen) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      triggerRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const viewerContent: ReactNode = renderViewer
    ? renderViewer(viewerProps)
    : Viewer
      ? <Viewer {...viewerProps} />
      : null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <style>{PRINT_VIEWER_CSS}</style>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={clsx(
          'flex flex-col overflow-hidden rounded-lg shadow-xl outline-none',
          'bg-bg-primary dark:bg-dark-bg-primary',
          isMaximized ? 'h-[98vh] w-[98vw] max-w-none' : 'h-[90vh] w-full max-w-4xl',
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Cabecera */}
        <div className="flex items-center justify-between border-b border-border-secondary px-4 py-3 dark:border-dark-border-primary">
          <h2 className="text-lg font-semibold text-content-primary dark:text-dark-content-primary">
            {title}
          </h2>
          <div className="flex items-center gap-1">
            {backendConfigured && (
              <button
                type="button"
                className="rounded p-1 text-content-tertiary transition-colors hover:bg-background-secondary hover:text-content-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-content-primary"
                onClick={() => setIsMaximized((m) => !m)}
                aria-label={isMaximized ? t('master_document.chrome.restoreSize') : t('master_document.chrome.maximize')}
                title={isMaximized ? t('master_document.chrome.restoreSize') : t('master_document.chrome.maximize')}
              >
                {isMaximized ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 9H5V5" /><path d="M15 9h4V5" /><path d="M9 15H5v4" /><path d="M15 15h4v4" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" />
                  </svg>
                )}
              </button>
            )}
            <button
              type="button"
              className="text-2xl leading-none text-content-tertiary transition-colors hover:text-content-primary dark:text-dark-content-secondary dark:hover:text-dark-content-primary"
              onClick={onClose}
              aria-label={t('master_document.chrome.close')}
            >
              ×
            </button>
          </div>
        </div>

        {/* Cuerpo */}
        {backendConfigured ? (
          <div className="sds-print-viewer min-h-0 flex-1 overflow-auto bg-background-secondary dark:bg-dark-bg-secondary">
            {viewerContent}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-background-secondary p-8 text-center dark:bg-dark-bg-secondary">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 9V2h12v7" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v8H6z" />
              </svg>
            </div>
            <p className="text-base font-medium text-content-primary dark:text-dark-content-primary">
              {notConfiguredMessage ?? t('master_document.document.printNotImplemented')}
            </p>
            {/* Subtítulo enlatado solo cuando se usa el mensaje por defecto;
                un mensaje personalizado controla el texto completo. */}
            {!notConfiguredMessage && (
              <p className="text-sm text-content-secondary dark:text-dark-content-secondary">
                {t('master_document.document.printAvailableSoon')}
              </p>
            )}
            <button
              type="button"
              className="mt-1 rounded-md bg-primary-inverse-background px-4 py-2 text-sm font-semibold text-primary-inverse-content transition-colors hover:bg-primary-700 dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90"
              onClick={onClose}
            >
              {t('master_document.chrome.understood')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
